import {
  dollarsToCents,
  parseCurrencyDollarsInput,
} from "../../../lib/financials";
import {
  type SavedProperty,
  type SavedPropertyInput,
  type SavedPropertyStatus,
  type SavedPropertyType,
} from "../../../lib/properties";
import type { PropertyAddressSelection } from "./property-address-provider";

type MonthlyCostSource = SavedPropertyInput["cost_overrides_source"];

export type PropertyFormState = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  selectedAddress: PropertyAddressSelection | null;
  state: string;
  postalCode: string;
  listPrice: string;
  listingURL: string;
  notes: string;
  tags: string;
  estimatedPropertyTaxesMonthly: string;
  estimatedHomeownersInsuranceMonthly: string;
  estimatedHOAMonthly: string;
  costOverridesSource: MonthlyCostSource;
  favorite: boolean;
  propertyType: SavedPropertyType;
  setAsTarget: boolean;
  status: SavedPropertyStatus;
};

export type PropertyMonthlyCostDefaults = {
  costOverridesSource?: MonthlyCostSource;
  estimatedHOAMonthlyCents?: number | null;
  estimatedHomeownersInsuranceMonthlyCents?: number | null;
  estimatedPropertyTaxesMonthlyCents?: number | null;
};

const PREVIEW_INSURANCE_RATE = 0.0042;
const PREVIEW_PROPERTY_TAX_RATE = 0.0108;

export function emptyFormState(
  defaults?: PropertyMonthlyCostDefaults,
): PropertyFormState {
  return {
    addressLine1: "",
    addressLine2: "",
    city: "",
    selectedAddress: null,
    state: "",
    postalCode: "",
    listPrice: "",
    listingURL: "",
    notes: "",
    tags: "",
    estimatedPropertyTaxesMonthly: monthlyCentsToFormValue(
      defaults?.estimatedPropertyTaxesMonthlyCents,
    ),
    estimatedHomeownersInsuranceMonthly: monthlyCentsToFormValue(
      defaults?.estimatedHomeownersInsuranceMonthlyCents,
    ),
    estimatedHOAMonthly: monthlyCentsToFormValue(
      defaults?.estimatedHOAMonthlyCents,
    ),
    costOverridesSource: defaults?.costOverridesSource ?? null,
    favorite: false,
    propertyType: "single_family",
    setAsTarget: false,
    status: "saved",
  };
}

export function formStateFromProperty(
  property: SavedProperty,
  defaults?: PropertyMonthlyCostDefaults,
): PropertyFormState {
  const monthlyDefaults =
    defaults ?? estimateMonthlyCostDefaultsFromListPrice(property.list_price);
  return {
    addressLine1: property.address_line1,
    addressLine2: property.address_line2 ?? "",
    city: property.city ?? "",
    selectedAddress: selectedAddressFromProperty(property),
    state: property.state ?? "",
    postalCode: property.postal_code ?? "",
    listPrice: String(property.list_price),
    listingURL: property.listing_url ?? "",
    notes: property.notes ?? "",
    tags: property.tags.join(", "),
    estimatedPropertyTaxesMonthly: monthlyCentsToFormValue(
      property.estimated_property_taxes_monthly_cents ??
        monthlyDefaults?.estimatedPropertyTaxesMonthlyCents,
    ),
    estimatedHomeownersInsuranceMonthly: monthlyCentsToFormValue(
      property.estimated_homeowners_insurance_monthly_cents ??
        monthlyDefaults?.estimatedHomeownersInsuranceMonthlyCents,
    ),
    estimatedHOAMonthly: monthlyCentsToFormValue(
      property.estimated_hoa_monthly_cents ??
        monthlyDefaults?.estimatedHOAMonthlyCents,
    ),
    costOverridesSource:
      property.cost_overrides_source ??
      (hasMonthlyCostDefaults(monthlyDefaults)
        ? (monthlyDefaults?.costOverridesSource ?? "quote")
        : null),
    favorite: property.favorite,
    propertyType: property.property_type,
    setAsTarget: false,
    status: property.status,
  };
}

export function validateFormState(formState: PropertyFormState) {
  if (!formState.addressLine1.trim()) {
    return "Address is required.";
  }
  if (!formState.listPrice.trim()) {
    return "List price is required.";
  }
  const listPrice = parseCurrencyDollarsInput(formState.listPrice);
  if (listPrice === null || !Number.isFinite(listPrice) || listPrice <= 0) {
    return "List price must be greater than zero.";
  }
  const listingURL = formState.listingURL.trim();
  if (listingURL) {
    try {
      const parsed = new URL(listingURL);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Listing URL must start with http:// or https://.";
      }
    } catch {
      return "Listing URL must start with http:// or https://.";
    }
  }
  const monthlyCostError =
    validateMonthlyCostField(
      formState.estimatedPropertyTaxesMonthly,
      "Property taxes",
    ) ??
    validateMonthlyCostField(
      formState.estimatedHomeownersInsuranceMonthly,
      "Homeowners insurance",
    ) ??
    validateMonthlyCostField(formState.estimatedHOAMonthly, "HOA");
  if (monthlyCostError) {
    return monthlyCostError;
  }
  return null;
}

export function buildSavedPropertyInput(
  formState: PropertyFormState,
): SavedPropertyInput {
  const monthlyCosts = resolveMonthlyCostCents(formState);
  const selectedAddress = formState.selectedAddress;

  return {
    address_line1: formState.addressLine1.trim(),
    address_line2: normalizeOptionalText(formState.addressLine2),
    city: normalizeOptionalText(formState.city),
    country: selectedAddress?.country.trim() || "US",
    formatted_address:
      selectedAddress?.formattedAddress || buildFormattedAddress(formState),
    latitude: selectedAddress?.latitude ?? null,
    longitude: selectedAddress?.longitude ?? null,
    state: normalizeOptionalText(formState.state),
    postal_code: normalizeOptionalText(formState.postalCode),
    favorite: formState.favorite,
    listing_url: normalizeOptionalText(formState.listingURL),
    notes: normalizeOptionalText(formState.notes),
    estimated_property_taxes_monthly_cents:
      monthlyCosts.estimatedPropertyTaxesMonthlyCents,
    estimated_homeowners_insurance_monthly_cents:
      monthlyCosts.estimatedHomeownersInsuranceMonthlyCents,
    estimated_hoa_monthly_cents: monthlyCosts.estimatedHOAMonthlyCents,
    cost_overrides_source: monthlyCosts.costOverridesSource,
    list_price: parseCurrencyDollarsInput(formState.listPrice) ?? 0,
    property_type: formState.propertyType,
    status: formState.status,
    tags: parseTags(formState.tags),
  };
}

export function buildSavedPropertyInputFromProperty(
  property: SavedProperty,
  overrides: Partial<SavedPropertyInput> = {},
): SavedPropertyInput {
  return {
    address_line1: property.address_line1,
    address_line2: property.address_line2 ?? null,
    city: property.city ?? null,
    country: property.country,
    favorite: property.favorite,
    formatted_address: property.formatted_address,
    latitude: property.latitude ?? null,
    list_price: property.list_price,
    listing_url: property.listing_url ?? null,
    longitude: property.longitude ?? null,
    notes: property.notes ?? null,
    estimated_property_taxes_monthly_cents:
      property.estimated_property_taxes_monthly_cents ?? null,
    estimated_homeowners_insurance_monthly_cents:
      property.estimated_homeowners_insurance_monthly_cents ?? null,
    estimated_hoa_monthly_cents: property.estimated_hoa_monthly_cents ?? null,
    cost_overrides_source: property.cost_overrides_source ?? null,
    postal_code: property.postal_code ?? null,
    property_type: property.property_type,
    state: property.state ?? null,
    status: property.status,
    tags: [...property.tags],
    ...overrides,
  };
}

export function estimateMonthlyCostDefaultsFromListPrice(
  listPrice: number | null | undefined,
): PropertyMonthlyCostDefaults | null {
  if (
    typeof listPrice !== "number" ||
    !Number.isFinite(listPrice) ||
    listPrice <= 0
  ) {
    return null;
  }
  return {
    costOverridesSource: "quote",
    estimatedPropertyTaxesMonthlyCents: dollarsToCents(
      Math.round((listPrice * PREVIEW_PROPERTY_TAX_RATE) / 12),
    ),
    estimatedHomeownersInsuranceMonthlyCents: dollarsToCents(
      Math.round((listPrice * PREVIEW_INSURANCE_RATE) / 12),
    ),
    estimatedHOAMonthlyCents: null,
  };
}

// ── Private helpers ────────────────────────────────────────────────────────────

export function monthlyCentsToFormValue(value: number | null | undefined) {
  if (typeof value !== "number") {
    return "";
  }
  const dollars = value / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

function resolveMonthlyCostCents(formState: PropertyFormState) {
  const parsed = {
    estimatedPropertyTaxesMonthlyCents: parseMonthlyCostCents(
      formState.estimatedPropertyTaxesMonthly,
    ),
    estimatedHomeownersInsuranceMonthlyCents: parseMonthlyCostCents(
      formState.estimatedHomeownersInsuranceMonthly,
    ),
    estimatedHOAMonthlyCents: parseMonthlyCostCents(
      formState.estimatedHOAMonthly,
    ),
  };
  const defaults = estimateMonthlyCostDefaultsFromListPrice(
    parseCurrencyDollarsInput(formState.listPrice),
  );
  const estimatedPropertyTaxesMonthlyCents =
    parsed.estimatedPropertyTaxesMonthlyCents ??
    defaults?.estimatedPropertyTaxesMonthlyCents ??
    null;
  const estimatedHomeownersInsuranceMonthlyCents =
    parsed.estimatedHomeownersInsuranceMonthlyCents ??
    defaults?.estimatedHomeownersInsuranceMonthlyCents ??
    null;
  const estimatedHOAMonthlyCents =
    parsed.estimatedHOAMonthlyCents ??
    defaults?.estimatedHOAMonthlyCents ??
    null;
  const hasAnyCost =
    estimatedPropertyTaxesMonthlyCents !== null ||
    estimatedHomeownersInsuranceMonthlyCents !== null ||
    estimatedHOAMonthlyCents !== null;

  return {
    estimatedPropertyTaxesMonthlyCents,
    estimatedHomeownersInsuranceMonthlyCents,
    estimatedHOAMonthlyCents,
    costOverridesSource: hasAnyCost
      ? (formState.costOverridesSource ??
        defaults?.costOverridesSource ??
        "quote")
      : null,
  };
}

function hasMonthlyCostDefaults(
  defaults: PropertyMonthlyCostDefaults | null | undefined,
) {
  return (
    defaults?.estimatedPropertyTaxesMonthlyCents != null ||
    defaults?.estimatedHomeownersInsuranceMonthlyCents != null ||
    defaults?.estimatedHOAMonthlyCents != null
  );
}

function normalizeOptionalText(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function validateMonthlyCostField(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return `${label} must be zero or greater.`;
  }
  return null;
}

function parseMonthlyCostCents(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return Math.round(Number(trimmed) * 100);
}

function parseTags(value: string) {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of value.split(",")) {
    const tag = part.trim();
    if (!tag || seen.has(tag)) {
      continue;
    }
    seen.add(tag);
    tags.push(tag);
  }
  return tags;
}

function buildFormattedAddress(formState: PropertyFormState) {
  return [
    formState.addressLine1,
    formState.city,
    formState.state,
    formState.postalCode,
  ]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ");
}

function selectedAddressFromProperty(
  property: SavedProperty,
): PropertyAddressSelection {
  return {
    addressLine1: property.address_line1,
    addressLine2: property.address_line2 ?? "",
    city: property.city ?? "",
    country: property.country,
    formattedAddress: property.formatted_address,
    latitude: property.latitude ?? null,
    longitude: property.longitude ?? null,
    postalCode: property.postal_code ?? "",
    state: property.state ?? "",
  };
}

